// Procedural ambient audio using Web Audio API — no external files needed
// Returns { start, stop, setVolume } controllers

export function createRain(ctx) {
    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    // Pink-ish noise
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99 * b0 + 0.029591 * white;
        b1 = 0.96 * b1 + 0.111928 * white;
        b2 = 0.57 * b2 + 0.482443 * white;
        data[i] = (b0 + b1 + b2) * 0.15;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;

    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 2400;
    lp.Q.value = 0.8;

    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 300;

    // slow amplitude modulation for a natural feel
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.15;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.08;
    lfo.connect(lfoGain);

    const gain = ctx.createGain();
    gain.gain.value = 0.5;
    lfoGain.connect(gain.gain);

    noise.connect(hp).connect(lp).connect(gain);
    noise.start();
    lfo.start();

    return {
        node: gain,
        stop: () => { try { noise.stop(); lfo.stop(); } catch (e) { console.debug("[ambient] rain stop", e); } },
    };
}

export function createForest(ctx) {
    // Softer pink noise = wind + occasional bird chirps
    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99 * b0 + 0.029591 * white;
        b1 = 0.99 * b1 + 0.032534 * white;
        b2 = 0.88 * b2 + 0.202443 * white;
        data[i] = (b0 + b1 + b2) * 0.11;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;

    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1200;

    const gain = ctx.createGain();
    gain.gain.value = 0.55;

    noise.connect(lp).connect(gain);
    noise.start();

    // Bird chirps scheduler
    let stopped = false;
    const chirp = () => {
        if (stopped) return;
        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        const base = 1400 + Math.random() * 1600;
        osc.frequency.setValueAtTime(base, now);
        osc.frequency.exponentialRampToValueAtTime(base * (0.6 + Math.random() * 0.7), now + 0.15);
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.06, now + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
        osc.connect(g).connect(gain);
        osc.start(now);
        osc.stop(now + 0.25);
        setTimeout(chirp, 2500 + Math.random() * 5500);
    };
    setTimeout(chirp, 1500);

    return {
        node: gain,
        stop: () => { stopped = true; try { noise.stop(); } catch (e) { console.debug("[ambient] forest stop", e); } },
    };
}

export function createLofi(ctx) {
    // Soft ambient chord pad
    const notes = [220, 277.18, 329.63, 415.30]; // Am7 vibe
    const gain = ctx.createGain();
    gain.gain.value = 0.14;

    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 1600;

    const oscs = notes.map((f, i) => {
        const o = ctx.createOscillator();
        o.type = i % 2 === 0 ? "sine" : "triangle";
        o.frequency.value = f;
        const g = ctx.createGain();
        g.gain.value = 0.25;
        // slow detune LFO for warmth
        const lfo = ctx.createOscillator();
        lfo.frequency.value = 0.08 + i * 0.02;
        const lfoG = ctx.createGain();
        lfoG.gain.value = 1.5;
        lfo.connect(lfoG).connect(o.detune);
        o.connect(g).connect(filter);
        o.start();
        lfo.start();
        return { o, lfo };
    });
    filter.connect(gain);

    // Add subtle vinyl noise
    const bufferSize = ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.04;
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;
    const nHp = ctx.createBiquadFilter();
    nHp.type = "highpass"; nHp.frequency.value = 3000;
    const nG = ctx.createGain(); nG.gain.value = 0.5;
    noise.connect(nHp).connect(nG).connect(gain);
    noise.start();

    return {
        node: gain,
        stop: () => { try { noise.stop(); oscs.forEach(({ o, lfo }) => { o.stop(); lfo.stop(); }); } catch (e) { console.debug("[ambient] lofi stop", e); } },
    };
}

export function createAmbient(kind) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    const ctx = new AC();
    const master = ctx.createGain();
    master.gain.value = 0.5;
    master.connect(ctx.destination);

    let src;
    if (kind === "rain") src = createRain(ctx);
    else if (kind === "forest") src = createForest(ctx);
    else if (kind === "lofi") src = createLofi(ctx);
    else return null;

    src.node.connect(master);

    return {
        setVolume: (v) => { master.gain.setTargetAtTime(v, ctx.currentTime, 0.1); },
        stop: async () => {
            master.gain.setTargetAtTime(0, ctx.currentTime, 0.15);
            setTimeout(() => { src.stop(); ctx.close(); }, 400);
        },
        resume: () => { if (ctx.state === "suspended") ctx.resume(); },
    };
}
